// src/lib/api.js - VERSÃO CONSOLIDADA COMPLETA CORRIGIDA COM ATUALIZAÇÕES
// FUNÇÃO: Centralizar todas as chamadas de API para MongoDB e Supabase

import AsyncStorage from '@react-native-async-storage/async-storage';
import { createClient } from '@supabase/supabase-js';
import {
  transformFormToApi,
  transformResponseToApi,
  transformApiToForm,
  transformApiToResponse,
} from '../utils/transformers';

// URL base da API no Heroku
const API_URL = 'https://avaqualis-server-5b5d8ca54885.herokuapp.com/api';

// Configuração do Supabase
const SUPABASE_URL = 'https://seu-projeto.supabase.co';
const SUPABASE_KEY = 'sua-chave-api-supabase';
const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

// Função auxiliar para fazer requisições HTTP
const fetchWithTimeout = async (url, options = {}, timeout = 30000) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });

    clearTimeout(id);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    clearTimeout(id);
    if (error.name === 'AbortError') {
      throw new Error('Tempo limite de requisição excedido');
    }
    throw error;
  }
};

// Função auxiliar para atualizar cache - MODIFICADA CONFORME SOLICITADO
const updateResponseCache = async response => {
  try {
    // Atualizar resposta individual
    await AsyncStorage.setItem(`cached_response_${response.id}`, JSON.stringify(response));

    // Atualizar lista geral de respostas
    const cachedResponses = (await AsyncStorage.getItem('cached_responses')) || '[]';
    let responses = JSON.parse(cachedResponses);

    // IMPORTANTE: Remover apenas respostas do MESMO avaliador
    responses = responses.filter(
      r => !(r.formId === response.formId && r.avaliador?.id === response.avaliador?.id) // Verificar avaliador
    );

    // Adicionar nova versão
    responses.push(response);
    // Garantir que a validação seja preservada no cache
    console.log('📦 Atualizando cache com validação:', {
      responseId: response.id,
      validadoPor: response.validacao?.validadoPor?.nome,
      dataValidacao: response.validacao?.dataValidacao,
    });

    await AsyncStorage.setItem('cached_responses', JSON.stringify(responses));

    // Atualizar cache específico do formulário
    const formCacheKey = `cached_responses_form_${response.formId}`;
    const cachedFormResponses = (await AsyncStorage.getItem(formCacheKey)) || '[]';
    let formResponses = JSON.parse(cachedFormResponses);

    // Remover versão antiga
    formResponses = formResponses.filter(r => r.avaliador?.id !== response.avaliador?.id);

    // Adicionar nova versão
    formResponses.push(response);

    await AsyncStorage.setItem(formCacheKey, JSON.stringify(formResponses));

    // ADICIONAR: Forçar atualização no StorageManager
    try {
      const StorageManager = await import('../utils/StorageManager').then(m => m.default);

      // Atualizar resposta no StorageManager também
      await StorageManager.saveResponse({
        ...response,
        synced: true,
        syncedAt: response.syncedAt || new Date().toISOString(),
      });

      // Forçar recarga de estatísticas
      if (StorageManager.forceReloadFormStats) {
        await StorageManager.forceReloadFormStats(response.formId);
      }

      console.log('✅ Cache e StorageManager atualizados');
    } catch (smError) {
      console.warn('⚠️ Erro ao atualizar StorageManager:', smError);
    }
  } catch (error) {
    console.warn('⚠️ Erro ao atualizar cache:', error);
  }
};

// NOVA FUNÇÃO: Verificar se resposta já existe antes de enviar
export const checkResponseExists = async (formId, avaliadorId) => {
  try {
    const responses = await fetchWithTimeout(
      `${API_URL}/responses/form/${formId}/avaliador/${avaliadorId}`,
      { method: 'GET' }
    );

    return {
      exists: responses && responses.length > 0,
      responseId: responses[0]?.id || null,
    };
  } catch (error) {
    return { exists: false, responseId: null };
  }
};

// NOVA FUNÇÃO: Verificar status de sincronização
export const getResponseSyncStatus = async (formId, avaliadorId) => {
  try {
    console.log(
      `🔍 Verificando status de sincronização para formulário ${formId}, avaliador ${avaliadorId}`
    );

    // Buscar resposta local
    let localResponse = null;
    try {
      const StorageManager = await import('../utils/StorageManager').then(m => m.default);
      const responses = await StorageManager.getResponsesByFormId(formId);
      localResponse = responses.find(r => r.avaliador?.id === avaliadorId);
    } catch (error) {
      console.warn('⚠️ Erro ao buscar resposta local:', error);
    }

    // Buscar resposta do servidor
    let serverResponse = null;
    try {
      const responses = await fetchWithTimeout(
        `${API_URL}/responses/form/${formId}/avaliador/${avaliadorId}`,
        { method: 'GET' }
      );
      if (responses && responses.length > 0) {
        serverResponse = responses[0];
      }
    } catch (error) {
      console.warn('⚠️ Erro ao buscar resposta do servidor:', error);
    }

    // Determinar status
    if (!localResponse && !serverResponse) {
      return { status: 'not-started', hasLocal: false, hasServer: false };
    }

    if (localResponse && !serverResponse) {
      return { status: 'local-only', hasLocal: true, hasServer: false };
    }

    if (!localResponse && serverResponse) {
      return { status: 'server-only', hasLocal: false, hasServer: true };
    }

    // Ambos existem - comparar datas
    const localDate = new Date(localResponse.lastModified || localResponse.createdAt);
    const serverDate = new Date(serverResponse.lastModified || serverResponse.createdAt);

    if (Math.abs(localDate - serverDate) < 10000) {
      // 10 segundos de tolerância
      return { status: 'synced', hasLocal: true, hasServer: true };
    }

    if (localDate > serverDate) {
      return { status: 'local-newer', hasLocal: true, hasServer: true };
    }

    return { status: 'cloud-newer', hasLocal: true, hasServer: true };
  } catch (error) {
    console.error('❌ Erro ao verificar status de sincronização:', error);
    return { status: 'error', error: error.message };
  }
};

// NOVA FUNÇÃO: Buscar status de sincronização detalhado
export const getDetailedSyncStatus = async (formId, avaliadorId) => {
  try {
    console.log(
      `🔍 Buscando status detalhado de sincronização para formulário ${formId}, avaliador ${avaliadorId}`
    );

    // Buscar resposta local
    let localResponse = null;
    try {
      const StorageManager = await import('../utils/StorageManager').then(m => m.default);
      const responses = await StorageManager.getResponsesByFormId(formId);
      localResponse = responses.find(r => r.avaliador?.id === avaliadorId);
    } catch (error) {
      console.warn('⚠️ Erro ao buscar resposta local:', error);
    }

    // Se não houver resposta local, retornar status vazio
    if (!localResponse) {
      return {
        status: 'not-started',
        hasLocal: false,
        hasServer: false,
        localResponse: null,
        serverResponse: null,
      };
    }

    // Buscar resposta do servidor
    let serverResponse = null;
    try {
      const responses = await fetchWithTimeout(
        `${API_URL}/responses/form/${formId}/avaliador/${avaliadorId}`,
        { method: 'GET' }
      );
      if (responses && responses.length > 0) {
        serverResponse = responses[0];
      }
    } catch (error) {
      console.warn('⚠️ Servidor offline ou erro ao buscar:', error);
      return {
        status: 'local-only',
        hasLocal: true,
        hasServer: false,
        localResponse: localResponse,
        serverResponse: null,
      };
    }

    // Se não há resposta no servidor
    if (!serverResponse) {
      return {
        status: 'local-only',
        hasLocal: true,
        hasServer: false,
        localResponse: localResponse,
        serverResponse: null,
      };
    }

    // Comparar conteúdo das respostas
    const localAnswers = JSON.stringify(localResponse.answers || {});
    const serverAnswers = JSON.stringify(serverResponse.answers || {});

    // Se o conteúdo é idêntico
    if (localAnswers === serverAnswers) {
      return {
        status: 'synced',
        hasLocal: true,
        hasServer: true,
        localResponse: localResponse,
        serverResponse: serverResponse,
      };
    }

    // Comparar datas para determinar qual é mais recente
    const localDate = new Date(localResponse.lastModified || localResponse.createdAt);
    const serverDate = new Date(serverResponse.lastModified || serverResponse.createdAt);

    // Margem de 10 segundos para considerar sincronizado
    if (Math.abs(localDate - serverDate) < 10000) {
      return {
        status: 'synced',
        hasLocal: true,
        hasServer: true,
        localResponse: localResponse,
        serverResponse: serverResponse,
      };
    }

    if (localDate > serverDate) {
      return {
        status: 'local-newer',
        hasLocal: true,
        hasServer: true,
        localResponse: localResponse,
        serverResponse: serverResponse,
      };
    }

    return {
      status: 'cloud-newer',
      hasLocal: true,
      hasServer: true,
      localResponse: localResponse,
      serverResponse: serverResponse,
    };
  } catch (error) {
    console.error('❌ Erro ao verificar status detalhado de sincronização:', error);
    return {
      status: 'error',
      error: error.message,
      hasLocal: false,
      hasServer: false,
    };
  }
};

// OPERAÇÕES RELACIONADAS A PROCESSOS
export const getProcessos = async () => {
  try {
    console.log('🔄 Buscando processos únicos...');
    const result = await fetchWithTimeout(`${API_URL}/processos`);
    return result;
  } catch (error) {
    console.error('❌ Erro ao buscar processos:', error);
    return [];
  }
};

export const updateProcessoNome = async (processoId, novoNome) => {
  try {
    console.log(`🔄 Atualizando nome do processo ${processoId}...`);
    const result = await fetchWithTimeout(`${API_URL}/processos/${processoId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ nome: novoNome }),
    });
    return result;
  } catch (error) {
    console.error('❌ Erro ao atualizar processo:', error);
    throw error;
  }
};

// OPERAÇÕES RELACIONADAS A FORMULÁRIOS (forms)
export const getForms = async () => {
  try {
    console.log('🔄 Buscando formulários no servidor...');
    const result = await fetchWithTimeout(`${API_URL}/forms`);

    // Transformar os resultados da API para o formato do app
    const formattedForms = result.map(form => transformApiToForm(form));

    // Atualizar cache local
    try {
      await AsyncStorage.setItem('cached_forms', JSON.stringify(formattedForms));
    } catch (storageError) {
      console.warn('⚠️ Erro ao atualizar cache local de formulários:', storageError);
    }

    return formattedForms;
  } catch (error) {
    console.error('❌ Erro ao buscar formulários:', error);

    // Tentar ler do armazenamento local em caso de falha
    try {
      const cachedForms = await AsyncStorage.getItem('cached_forms');
      if (cachedForms) {
        return JSON.parse(cachedForms);
      }
    } catch (storageError) {
      console.error('❌ Erro ao buscar formulários do cache:', storageError);
    }

    throw error;
  }
};
export const getFormById = async id => {
  try {
    console.log(`🔄 Buscando formulário ${id}...`);
    const result = await fetchWithTimeout(`${API_URL}/forms/${id}`);

    // ✅ LOG CRÍTICO - Verificar dados do servidor
    console.log('🔍 Dados do servidor:', {
      id: result.id,
      questionsCount: result.questions?.length || 0,
      firstQuestion: result.questions?.[0]
        ? {
            id: result.questions[0].id,
            grupoRequisito: result.questions[0].grupoRequisito,
            sugestaoRoteiro: result.questions[0].sugestaoRoteiro,
          }
        : 'Sem questões',
    });

    // Preservar ID
    result.id = id;

    // ✅ TRANSFORMAR PRIMEIRO (antes de verificar)
    const formattedForm = transformApiToForm(result);

    // ✅ VERIFICAÇÃO APÓS TRANSFORMAÇÃO (agora formattedForm existe)
    if (formattedForm.requirements && formattedForm.requirements.length > 0) {
      console.log('✅ Após transformação - Primeira requirement:', {
        id: formattedForm.requirements[0].id,
        grupoRequisito: formattedForm.requirements[0].grupoRequisito,
        sugestaoRoteiro: formattedForm.requirements[0].sugestaoRoteiro,
      });
    }

    // Verificar se tem requisitos depois da transformação
    if (!formattedForm.requirements || formattedForm.requirements.length === 0) {
      console.warn(`⚠️ O formulário ${id} não tem requisitos após transformação!`);

      // Tentar processar diretamente as questões se disponíveis
      if (result.questions && result.questions.length > 0) {
        console.log(
          `🔧 Corrigindo: Processando ${result.questions.length} questões manualmente...`
        );

        formattedForm.requirements = result.questions.map((q, index) => ({
          id: q.id || q.code || `req-${index}`,
          codigo: q.code || `${index + 1}`,
          question: q.text || 'Sem texto',
          premissa: q.premissa || q.notes || '',
          fontes: q.fontes || '',
          segmento: q.segmento || '',
          grupoRequisito: q.grupoRequisito || '', // ✅ PRESERVADO
          sugestaoRoteiro: q.sugestaoRoteiro || '', // ✅ PRESERVADO
          isEssential: q.isEssential || false,
          sectionInfo: q.sectionInfo ||
            extractSectionInfo(q.category) || {
              tipo: 'Não categorizado',
              categoria: 'Geral',
              subcategoria: '',
            },
          order: q.order || index + 1,
        }));

        console.log(`✅ Recuperadas ${formattedForm.requirements.length} questões manualmente`);
      }
    }

    // Garantir que o ID seja mantido
    formattedForm.id = id;

    // ADICIONAR: Garantir que sections existam
    if (!formattedForm.sections || formattedForm.sections.length === 0) {
      console.warn(`⚠️ Formulário ${id} sem sections, tentando gerar...`);

      // Gerar sections a partir de questions/requirements
      if (formattedForm.questions && formattedForm.questions.length > 0) {
        const sectionsMap = new Map();

        formattedForm.questions.forEach(q => {
          const sectionKey = `${q.sectionInfo?.tipo || 'Geral'}-${q.sectionInfo?.categoria || 'Geral'}`;
          if (!sectionsMap.has(sectionKey)) {
            sectionsMap.set(sectionKey, {
              id: `section-${sectionsMap.size + 1}`,
              tipo: q.sectionInfo?.tipo || 'Geral',
              categoria: q.sectionInfo?.categoria || 'Geral',
              subcategoria: q.sectionInfo?.subcategoria || '',
              questions: [],
            });
          }
          sectionsMap.get(sectionKey).questions.push(q);
        });

        formattedForm.sections = Array.from(sectionsMap.values());
        console.log(`✅ ${formattedForm.sections.length} sections geradas para o formulário`);
      }
    }

    // Atualizar cache local
    try {
      await AsyncStorage.setItem(`cached_form_${id}`, JSON.stringify(formattedForm));
    } catch (storageError) {
      console.warn('⚠️ Erro ao atualizar cache local do formulário:', storageError);
    }

    // ✅ ÚNICO RETURN no fluxo normal
    return formattedForm;
  } catch (error) {
    console.error(`❌ Erro ao buscar formulário ${id}:`, error);

    // Tentar ler do armazenamento local em caso de falha
    try {
      console.log(`🔍 Tentando recuperar formulário ${id} do cache local...`);
      const cachedForm = await AsyncStorage.getItem(`cached_form_${id}`);
      if (cachedForm) {
        const parsedForm = JSON.parse(cachedForm);
        console.log(
          `✅ Formulário ${id} recuperado do cache com ${parsedForm.requirements?.length || 0} requisitos`
        );
        return parsedForm; // ✅ SEGUNDO RETURN (fallback em caso de erro)
      }
    } catch (storageError) {
      console.error('❌ Erro ao buscar formulário do cache:', storageError);
    }

    // ✅ Se tudo falhar, propagar o erro
    throw error;
  }
};
// Função auxiliar para extrair informações de seção
function extractSectionInfo(category) {
  if (!category) return { tipo: '', categoria: '', subcategoria: '' };

  try {
    const parts = category.split(' - ');
    return {
      tipo: parts[0] || '',
      categoria: parts[1] || '',
      subcategoria: parts[2] || '',
    };
  } catch (error) {
    console.error('❌ Erro ao extrair informações de seção:', error);
    return { tipo: '', categoria: '', subcategoria: '' };
  }
}

export const createForm = async formData => {
  try {
    console.log('🔄 Criando formulário no servidor...');
    console.log('📋 ID do formulário:', formData.id);

    // Garantir que temos um ID único
    if (!formData.id) {
      throw new Error('ID do formulário não fornecido');
    }

    // Transformar os dados do app para o formato da API
    const apiData = transformFormToApi(formData);

    // Garantir que o ID seja preservado
    apiData.id = formData.id;

    const result = await fetchWithTimeout(`${API_URL}/forms`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(apiData),
    });

    // Transformar o resultado da API de volta para o formato do app
    const formattedResult = transformApiToForm(result);

    // Atualizar cache local
    try {
      // Salvar o formulário individual
      await AsyncStorage.setItem(
        `cached_form_${formattedResult.id}`,
        JSON.stringify(formattedResult)
      );

      // Atualizar lista de formulários
      const cachedForms = await AsyncStorage.getItem('cached_forms');
      if (cachedForms) {
        const forms = JSON.parse(cachedForms);
        forms.push(formattedResult);
        await AsyncStorage.setItem('cached_forms', JSON.stringify(forms));
      }
    } catch (storageError) {
      console.warn('⚠️ Erro ao atualizar cache local:', storageError);
    }

    return formattedResult;
  } catch (error) {
    console.error('❌ Erro ao criar formulário:', error);

    // Se for erro de ID duplicado, gerar novo ID e tentar novamente
    if (error.message && error.message.includes('duplicate')) {
      console.log('⚠️ ID duplicado detectado, gerando novo ID...');
      const newId = `custom-form-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
      formData.id = newId;

      // Tentar novamente com novo ID
      return await createForm(formData);
    }

    // Salvar localmente para sincronização posterior
    try {
      const pendingUploads = (await AsyncStorage.getItem('pending_uploads')) || '[]';
      const uploads = JSON.parse(pendingUploads);
      uploads.push({
        type: 'form',
        data: formData,
        timestamp: Date.now(),
      });
      await AsyncStorage.setItem('pending_uploads', JSON.stringify(uploads));
      console.log('✅ Formulário salvo localmente para sincronização futura');
    } catch (storageError) {
      console.error('❌ Erro ao salvar formulário localmente:', storageError);
    }

    throw error;
  }
};

export const updateForm = async (id, formData) => {
  try {
    console.log(`🔄 Atualizando formulário ${id} no servidor...`);

    // Verificar se há questions (perguntas) no formData
    if (formData.questions) {
      console.log(`📋 Formulário já contém ${formData.questions.length} questões para atualização`);
    } else if (formData.requirements) {
      console.log(`📋 Transformando ${formData.requirements.length} requisitos em questões...`);

      // Transformar os dados do app para o formato da API
      const apiData = transformFormToApi(formData);
      formData = apiData; // Usar o formData transformado
    } else {
      console.warn('⚠️ ALERTA: Não há questões nem requisitos para atualizar no formulário!');

      // Tentar recuperar as questões do servidor antes de atualizar
      try {
        const existingForm = await getFormById(id);
        if (existingForm && existingForm.requirements && existingForm.requirements.length > 0) {
          console.log(
            `🔄 Recuperando ${existingForm.requirements.length} requisitos do formulário existente`
          );

          // Adicionar os requisitos existentes ao formData
          formData.requirements = existingForm.requirements;

          // Transformar novamente para o formato da API
          const apiData = transformFormToApi(formData);
          formData = apiData;
        }
      } catch (fetchError) {
        console.error('❌ Erro ao tentar recuperar questões existentes:', fetchError);
      }
    }

    console.log(`🔍 ID para atualização: ${id}`);
    console.log(`🔍 URL para atualização: ${API_URL}/forms/${id}`);
    console.log(`📦 Dados a serem enviados: ${formData.questions?.length || 0} questões`);

    const result = await fetchWithTimeout(`${API_URL}/forms/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    });

    console.log(`✅ Servidor retornou: ${result.questions?.length || 0} questões`);

    // Transformar o resultado da API de volta para o formato do app
    const formattedResult = transformApiToForm(result);

    // Atualizar cache local
    try {
      await AsyncStorage.setItem(`cached_form_${id}`, JSON.stringify(formattedResult));

      // Atualizar na lista de formulários
      const cachedForms = await AsyncStorage.getItem('cached_forms');
      if (cachedForms) {
        let forms = JSON.parse(cachedForms);
        forms = forms.map(form => (form.id === id ? formattedResult : form));
        await AsyncStorage.setItem('cached_forms', JSON.stringify(forms));
      }
    } catch (storageError) {
      console.warn('⚠️ Erro ao atualizar cache local:', storageError);
    }

    return formattedResult;
  } catch (error) {
    console.error(`❌ Erro ao atualizar formulário ${id}:`, error);

    // Salvar localmente para sincronização posterior
    try {
      const pendingUpdates = (await AsyncStorage.getItem('pending_updates')) || '[]';
      const updates = JSON.parse(pendingUpdates);
      updates.push({
        type: 'form',
        id: id,
        data: formData,
        timestamp: Date.now(),
      });
      await AsyncStorage.setItem('pending_updates', JSON.stringify(updates));
      console.log('✅ Atualização salva localmente para sincronização futura');
    } catch (storageError) {
      console.error('❌ Erro ao salvar atualização localmente:', storageError);
    }

    throw error;
  }
};

// FUNÇÃO CORRIGIDA: Excluir formulário com verificação prévia
export const deleteForm = async id => {
  try {
    console.log(`🔄 Excluindo formulário ${id} do servidor...`);

    // PRIMEIRA VERIFICAÇÃO: Se o formulário existe no servidor
    try {
      const existingForm = await fetchWithTimeout(`${API_URL}/forms/${id}`, {
        method: 'GET',
      });

      if (!existingForm) {
        throw new Error('Formulário não encontrado no servidor');
      }

      console.log(`✅ Formulário ${id} encontrado no servidor, prosseguindo com exclusão...`);
    } catch (checkError) {
      if (checkError.message.includes('404') || checkError.message.includes('não encontrado')) {
        console.warn(`⚠️ Formulário ${id} não existe no servidor, removendo apenas localmente`);

        // Remover cache local mesmo que não exista no servidor
        try {
          await AsyncStorage.removeItem(`cached_form_${id}`);

          // Remover da lista de formulários
          const cachedForms = await AsyncStorage.getItem('cached_forms');
          if (cachedForms) {
            let forms = JSON.parse(cachedForms);
            forms = forms.filter(form => form.id !== id);
            await AsyncStorage.setItem('cached_forms', JSON.stringify(forms));
          }
        } catch (storageError) {
          console.warn('⚠️ Erro ao atualizar cache local:', storageError);
        }

        return { message: 'Formulário removido localmente (não existia no servidor)' };
      }

      throw checkError;
    }

    // Se chegou aqui, o formulário existe no servidor, fazer a exclusão
    const result = await fetchWithTimeout(`${API_URL}/forms/${id}`, {
      method: 'DELETE',
    });

    // Atualizar cache local após exclusão bem-sucedida
    try {
      await AsyncStorage.removeItem(`cached_form_${id}`);

      // Remover da lista de formulários
      const cachedForms = await AsyncStorage.getItem('cached_forms');
      if (cachedForms) {
        let forms = JSON.parse(cachedForms);
        forms = forms.filter(form => form.id !== id);
        await AsyncStorage.setItem('cached_forms', JSON.stringify(forms));
      }
    } catch (storageError) {
      console.warn('⚠️ Erro ao atualizar cache local:', storageError);
    }

    console.log(`✅ Formulário ${id} excluído com sucesso do servidor`);
    return result;
  } catch (error) {
    console.error(`❌ Erro ao excluir formulário ${id}:`, error);

    // Salvar localmente para sincronização posterior apenas se não for erro 404
    if (!error.message.includes('404') && !error.message.includes('não encontrado')) {
      try {
        const pendingDeletes = (await AsyncStorage.getItem('pending_deletes')) || '[]';
        const deletes = JSON.parse(pendingDeletes);
        deletes.push({
          type: 'form',
          id: id,
          timestamp: Date.now(),
        });
        await AsyncStorage.setItem('pending_deletes', JSON.stringify(deletes));
        console.log('✅ Exclusão salva localmente para sincronização futura');
      } catch (storageError) {
        console.error('❌ Erro ao salvar exclusão localmente:', storageError);
      }
    }

    throw error;
  }
};

// NOVA FUNÇÃO: Enviar email de notificação para lideranças
export const sendLeadershipEmail = async (formId, customMessage, sentBy) => {
  try {
    console.log(`📧 Enviando email de notificação para formulário ${formId}...`);

    const result = await fetchWithTimeout(`${API_URL}/forms/${formId}/send-leadership-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customMessage,
        sentBy,
      }),
    });

    console.log('✅ Email enviado com sucesso:', result);

    // Atualizar cache local do formulário
    try {
      const cachedForm = await AsyncStorage.getItem(`cached_form_${formId}`);
      if (cachedForm) {
        const form = JSON.parse(cachedForm);
        if (!form.emailsSent) {
          form.emailsSent = [];
        }
        form.emailsSent.push({
          sentAt: result.sentAt,
          sentBy: sentBy,
          recipients: result.recipients,
          status: 'success',
        });
        await AsyncStorage.setItem(`cached_form_${formId}`, JSON.stringify(form));
      }
    } catch (storageError) {
      console.warn('⚠️ Erro ao atualizar cache local:', storageError);
    }

    return result;
  } catch (error) {
    console.error(`❌ Erro ao enviar email de notificação:`, error);
    throw error;
  }
};

// NOVA FUNÇÃO: Excluir formulário completo com todas as respostas
export const deleteFormComplete = async formId => {
  try {
    console.log(`🗑️ Excluindo formulário completo ${formId} com todas as respostas...`);

    // PRIMEIRA VERIFICAÇÃO: Se o formulário existe no servidor
    try {
      const existingForm = await fetchWithTimeout(`${API_URL}/forms/${formId}`, {
        method: 'GET',
      });

      if (!existingForm) {
        throw new Error('Formulário não encontrado no servidor');
      }

      console.log(
        `✅ Formulário ${formId} encontrado no servidor, prosseguindo com exclusão completa...`
      );
    } catch (checkError) {
      if (checkError.message.includes('404') || checkError.message.includes('não encontrado')) {
        console.warn(`⚠️ Formulário ${formId} não existe no servidor, removendo apenas localmente`);

        // Remover cache local completo
        await removeFormCompleteFromCache(formId);

        return { message: 'Formulário removido localmente (não existia no servidor)' };
      }

      throw checkError;
    }

    // Fazer exclusão completa no servidor
    const result = await fetchWithTimeout(`${API_URL}/forms/${formId}/complete`, {
      method: 'DELETE',
    });

    // Remover cache local completo
    await removeFormCompleteFromCache(formId);

    console.log(`✅ Formulário completo ${formId} excluído com sucesso`);
    return result;
  } catch (error) {
    console.error(`❌ Erro ao excluir formulário completo ${formId}:`, error);

    // Salvar localmente para sincronização posterior apenas se não for erro 404
    if (!error.message.includes('404') && !error.message.includes('não encontrado')) {
      try {
        const pendingCompleteDeletes =
          (await AsyncStorage.getItem('pending_complete_deletes')) || '[]';
        const deletes = JSON.parse(pendingCompleteDeletes);
        deletes.push({
          type: 'form_complete',
          formId: formId,
          timestamp: Date.now(),
        });
        await AsyncStorage.setItem('pending_complete_deletes', JSON.stringify(deletes));
        console.log('✅ Exclusão completa salva localmente para sincronização futura');
      } catch (storageError) {
        console.error('❌ Erro ao salvar exclusão completa localmente:', storageError);
      }
    }

    throw error;
  }
};

// Função auxiliar para remover formulário completo do cache
const removeFormCompleteFromCache = async formId => {
  try {
    // Remover formulário
    await AsyncStorage.removeItem(`cached_form_${formId}`);

    // Remover da lista de formulários
    const cachedForms = await AsyncStorage.getItem('cached_forms');
    if (cachedForms) {
      let forms = JSON.parse(cachedForms);
      forms = forms.filter(form => form.id !== formId);
      await AsyncStorage.setItem('cached_forms', JSON.stringify(forms));
    }

    // Remover todas as respostas relacionadas
    await AsyncStorage.removeItem(`cached_responses_form_${formId}`);

    // Atualizar lista geral de respostas
    const cachedResponses = await AsyncStorage.getItem('cached_responses');
    if (cachedResponses) {
      let responses = JSON.parse(cachedResponses);
      responses = responses.filter(r => r.formId !== formId);
      await AsyncStorage.setItem('cached_responses', JSON.stringify(responses));
    }

    console.log(`✅ Cache completo removido para formulário ${formId}`);
  } catch (storageError) {
    console.warn('⚠️ Erro ao atualizar cache local:', storageError);
  }
};

// NOVA FUNÇÃO: Atualizar avaliadores de um formulário
export const updateFormEvaluators = async (formId, avaliador1Id, avaliador2Id) => {
  try {
    console.log(`🔄 Atualizando avaliadores do formulário ${formId}...`);
    console.log(`👥 Novos avaliadores: ${avaliador1Id} e ${avaliador2Id}`);

    // Primeiro verificar se o formulário existe
    try {
      const existingForm = await fetchWithTimeout(`${API_URL}/forms/${formId}`, {
        method: 'GET',
      });

      if (!existingForm) {
        throw new Error('Formulário não encontrado no servidor');
      }
    } catch (checkError) {
      if (checkError.message.includes('404') || checkError.message.includes('não encontrado')) {
        throw new Error('Formulário não existe no servidor');
      }
      throw checkError;
    }

    // Buscar nomes dos avaliadores
    const { AVALIADORES } = await import('../data/EstruturaOrganizacional');
    const avaliador1 = AVALIADORES.find(a => a.id === avaliador1Id);
    const avaliador2 = AVALIADORES.find(a => a.id === avaliador2Id);

    if (!avaliador1 || !avaliador2) {
      throw new Error('Avaliadores não encontrados');
    }

    // Criar novo author (concatenação dos nomes)
    const newAuthor = `${avaliador1.nome} & ${avaliador2.nome}`;

    const updateData = {
      author: newAuthor,
      metadata: {
        avaliador1: avaliador1Id,
        avaliador2: avaliador2Id,
      },
    };

    const result = await fetchWithTimeout(`${API_URL}/forms/${formId}/evaluators`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updateData),
    });

    // Atualizar cache local
    try {
      // Atualizar formulário individual
      const cachedForm = await AsyncStorage.getItem(`cached_form_${formId}`);
      if (cachedForm) {
        const form = JSON.parse(cachedForm);
        form.author = newAuthor;
        if (!form.metadata) form.metadata = {};
        form.metadata.avaliador1 = avaliador1Id;
        form.metadata.avaliador2 = avaliador2Id;
        await AsyncStorage.setItem(`cached_form_${formId}`, JSON.stringify(form));
      }

      // Atualizar na lista de formulários
      const cachedForms = await AsyncStorage.getItem('cached_forms');
      if (cachedForms) {
        let forms = JSON.parse(cachedForms);
        forms = forms.map(form => {
          if (form.id === formId) {
            return {
              ...form,
              author: newAuthor,
              metadata: {
                ...form.metadata,
                avaliador1: avaliador1Id,
                avaliador2: avaliador2Id,
              },
            };
          }
          return form;
        });
        await AsyncStorage.setItem('cached_forms', JSON.stringify(forms));
      }
    } catch (storageError) {
      console.warn('⚠️ Erro ao atualizar cache local:', storageError);
    }

    return result;
  } catch (error) {
    console.error(`❌ Erro ao atualizar avaliadores do formulário ${formId}:`, error);

    // Salvar localmente para sincronização posterior apenas se não for erro 404
    if (!error.message.includes('404') && !error.message.includes('não encontrado')) {
      try {
        const pendingEvaluatorUpdates =
          (await AsyncStorage.getItem('pending_evaluator_updates')) || '[]';
        const updates = JSON.parse(pendingEvaluatorUpdates);
        updates.push({
          type: 'form_evaluators',
          formId: formId,
          avaliador1Id: avaliador1Id,
          avaliador2Id: avaliador2Id,
          timestamp: Date.now(),
        });
        await AsyncStorage.setItem('pending_evaluator_updates', JSON.stringify(updates));
        console.log('✅ Atualização de avaliadores salva localmente para sincronização futura');
      } catch (storageError) {
        console.error('❌ Erro ao salvar atualização de avaliadores localmente:', storageError);
      }
    }

    throw error;
  }
};

// NOVA FUNÇÃO: Atualizar metadados do formulário (título, descrição, etc)
export const updateFormMetadata = async (formId, updateData) => {
  try {
    console.log(`🔄 Atualizando metadados do formulário ${formId}...`);
    console.log(`📝 Dados para atualização:`, updateData);

    // Verificar se o formulário existe
    const existingForm = await fetchWithTimeout(`${API_URL}/forms/${formId}`, {
      method: 'GET',
    });

    if (!existingForm) {
      throw new Error('Formulário não encontrado no servidor');
    }

    // Preparar dados para atualização
    const formUpdate = {
      ...updateData,
      updatedAt: new Date().toISOString(),
    };

    // Usar a rota updateForm existente
    const result = await fetchWithTimeout(`${API_URL}/forms/${formId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formUpdate),
    });

    // Transformar resultado
    const formattedResult = transformApiToForm(result);

    // Atualizar cache local
    try {
      await AsyncStorage.setItem(`cached_form_${formId}`, JSON.stringify(formattedResult));

      const cachedForms = await AsyncStorage.getItem('cached_forms');
      if (cachedForms) {
        let forms = JSON.parse(cachedForms);
        forms = forms.map(form => (form.id === formId ? formattedResult : form));
        await AsyncStorage.setItem('cached_forms', JSON.stringify(forms));
      }
    } catch (storageError) {
      console.warn('⚠️ Erro ao atualizar cache local:', storageError);
    }

    return formattedResult;
  } catch (error) {
    console.error(`❌ Erro ao atualizar metadados do formulário ${formId}:`, error);
    throw error;
  }
};

// NOVA FUNÇÃO: Excluir respostas específicas de um avaliador para um formulário
export const deleteResponsesByEvaluator = async (formId, avaliadorId) => {
  try {
    console.log(
      `🗑️ Excluindo respostas do avaliador ${avaliadorId} para o formulário ${formId}...`
    );

    const result = await fetchWithTimeout(
      `${API_URL}/responses/form/${formId}/evaluator/${avaliadorId}`,
      {
        method: 'DELETE',
      }
    );

    // Atualizar cache local
    try {
      // Atualizar lista de respostas do formulário
      const cachedFormResponses = await AsyncStorage.getItem(`cached_responses_form_${formId}`);
      if (cachedFormResponses) {
        let formResponses = JSON.parse(cachedFormResponses);
        formResponses = formResponses.filter(r => r.avaliador?.id !== avaliadorId);
        await AsyncStorage.setItem(
          `cached_responses_form_${formId}`,
          JSON.stringify(formResponses)
        );
      }

      // Atualizar lista geral de respostas
      const cachedResponses = await AsyncStorage.getItem('cached_responses');
      if (cachedResponses) {
        let responses = JSON.parse(cachedResponses);
        responses = responses.filter(
          r => !(r.formId === formId && r.avaliador?.id === avaliadorId)
        );
        await AsyncStorage.setItem('cached_responses', JSON.stringify(responses));
      }
    } catch (storageError) {
      console.warn('⚠️ Erro ao atualizar cache local:', storageError);
    }

    return result;
  } catch (error) {
    console.error(
      `❌ Erro ao excluir respostas do avaliador ${avaliadorId} para o formulário ${formId}:`,
      error
    );

    // Salvar localmente para sincronização posterior
    try {
      const pendingEvaluatorDeletes =
        (await AsyncStorage.getItem('pending_evaluator_deletes')) || '[]';
      const deletes = JSON.parse(pendingEvaluatorDeletes);
      deletes.push({
        type: 'responses_by_evaluator',
        formId: formId,
        avaliadorId: avaliadorId,
        timestamp: Date.now(),
      });
      await AsyncStorage.setItem('pending_evaluator_deletes', JSON.stringify(deletes));
      console.log(
        '✅ Exclusão de respostas do avaliador salva localmente para sincronização futura'
      );
    } catch (storageError) {
      console.error(
        '❌ Erro ao salvar exclusão de respostas do avaliador localmente:',
        storageError
      );
    }

    throw error;
  }
};

// OPERAÇÕES RELACIONADAS A RESPOSTAS (responses)
export const getResponses = async () => {
  try {
    console.log('🔄 Buscando respostas no servidor...');
    const result = await fetchWithTimeout(`${API_URL}/responses`);

    // Transformar os resultados
    const formattedResponses = result.map(response => {
      const formatted = transformApiToResponse(response);

      // IMPORTANTE: Marcar como sincronizado se veio do servidor
      formatted.synced = true;
      formatted.syncedAt = formatted.syncedAt || formatted.lastModified || new Date().toISOString();

      return formatted;
    });

    // Atualizar cache local
    try {
      await AsyncStorage.setItem('cached_responses', JSON.stringify(formattedResponses));
    } catch (storageError) {
      console.warn('⚠️ Erro ao atualizar cache local de respostas:', storageError);
    }

    return formattedResponses;
  } catch (error) {
    console.error('❌ Erro ao buscar respostas:', error);

    // Tentar ler do armazenamento local em caso de falha
    try {
      const cachedResponses = await AsyncStorage.getItem('cached_responses');
      if (cachedResponses) {
        return JSON.parse(cachedResponses);
      }
    } catch (storageError) {
      console.error('❌ Erro ao buscar respostas do cache:', storageError);
    }

    throw error;
  }
};

export const getResponsesByFormId = async formId => {
  try {
    console.log(`🔄 Buscando respostas para o formulário ${formId}...`);
    const result = await fetchWithTimeout(`${API_URL}/responses/form/${formId}`);

    // Transformar os resultados da API para o formato do app
    const formattedResponses = result.map(response => transformApiToResponse(response));

    // IMPORTANTE: Garantir que o campo validacao seja preservado
    const responsesWithValidation = formattedResponses.map((response, index) => {
      const originalResponse = result[index];
      if (originalResponse && originalResponse.validacao) {
        response.validacao = originalResponse.validacao;
      }
      return response;
    });

    // Substituir formattedResponses por responsesWithValidation no resto da função
    const finalResponses = responsesWithValidation;

    // Atualizar cache local
    try {
      await AsyncStorage.setItem(
        `cached_responses_form_${formId}`,
        JSON.stringify(formattedResponses)
      );
    } catch (storageError) {
      console.warn('⚠️ Erro ao atualizar cache local de respostas:', storageError);
    }

    return finalResponses;
  } catch (error) {
    console.error(`❌ Erro ao buscar respostas para o formulário ${formId}:`, error);

    // Tentar ler do armazenamento local em caso de falha
    try {
      const cachedResponses = await AsyncStorage.getItem(`cached_responses_form_${formId}`);
      if (cachedResponses) {
        return JSON.parse(cachedResponses);
      }
    } catch (storageError) {
      console.error('❌ Erro ao buscar respostas do cache:', storageError);
    }

    return []; // Retornar array vazio em caso de erro
  }
};

// FUNÇÃO CORRIGIDA: Buscar respostas por formulário e avaliador
export const getResponsesByFormAndAvaliador = async (formId, avaliadorId) => {
  try {
    console.log(`🔄 Buscando respostas do formulário ${formId} para avaliador ${avaliadorId}...`);

    // Este endpoint deveria existir no servidor
    const result = await fetchWithTimeout(
      `${API_URL}/responses/form/${formId}/avaliador/${avaliadorId}`
    );

    // Transformar os resultados
    const formattedResponses = result.map(response => transformApiToResponse(response));

    // Atualizar cache local
    try {
      await AsyncStorage.setItem(
        `cached_responses_form_${formId}_avaliador_${avaliadorId}`,
        JSON.stringify(formattedResponses)
      );
    } catch (storageError) {
      console.warn('⚠️ Erro ao atualizar cache local:', storageError);
    }

    return formattedResponses;
  } catch (error) {
    console.error(`❌ Erro ao buscar respostas:`, error);

    // Tentar ler do cache
    try {
      const cached = await AsyncStorage.getItem(
        `cached_responses_form_${formId}_avaliador_${avaliadorId}`
      );
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (cacheError) {
      console.error('❌ Erro ao buscar do cache:', cacheError);
    }

    return [];
  }
};

// Buscar respostas pendentes de validação
export const getResponsesPendingValidation = async () => {
  try {
    console.log('🔄 Buscando respostas pendentes de validação...');
    const result = await fetchWithTimeout(`${API_URL}/responses/pending-validation`);

    const formattedResponses = result.map(response => transformApiToResponse(response));

    return formattedResponses;
  } catch (error) {
    console.error('❌ Erro ao buscar respostas pendentes:', error);
    return [];
  }
};

// Buscar respostas já validadas
export const getValidatedResponses = async () => {
  try {
    console.log('🔄 Buscando respostas validadas...');
    const result = await fetchWithTimeout(`${API_URL}/responses/validated`);

    const formattedResponses = result.map(response => transformApiToResponse(response));

    return formattedResponses;
  } catch (error) {
    console.error('❌ Erro ao buscar respostas validadas:', error);
    return [];
  }
};

// Validar uma resposta
export const validateResponse = async (responseId, validador) => {
  try {
    console.log(`🔄 Validando resposta ${responseId}...`);
    console.log('👤 Dados do validador:', validador);

    const result = await fetchWithTimeout(`${API_URL}/responses/${responseId}/validate`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ validador }),
    });

    console.log('✅ Resposta da API:', result);

    const transformedResponse = transformApiToResponse(result);

    // Garantir que a validação foi incluída na resposta transformada
    if (result.validacao) {
      transformedResponse.validacao = result.validacao;
    }

    // Atualizar o cache da resposta
    await updateResponseCache(transformedResponse);

    return transformedResponse;
  } catch (error) {
    console.error('❌ Erro ao validar resposta:', error);
    throw error;
  }
};

export const getResponsesByAvaliadorId = async avaliadorId => {
  try {
    console.log(`🔄 Buscando respostas do avaliador ${avaliadorId}...`);
    const result = await fetchWithTimeout(`${API_URL}/responses/avaliador/${avaliadorId}`);

    // Transformar os resultados da API para o formato do app
    const formattedResponses = result.map(response => transformApiToResponse(response));

    // Atualizar cache local
    try {
      await AsyncStorage.setItem(
        `cached_responses_avaliador_${avaliadorId}`,
        JSON.stringify(formattedResponses)
      );
    } catch (storageError) {
      console.warn('⚠️ Erro ao atualizar cache local de respostas do avaliador:', storageError);
    }

    return formattedResponses;
  } catch (error) {
    console.error(`❌ Erro ao buscar respostas do avaliador ${avaliadorId}:`, error);

    // Tentar ler do armazenamento local em caso de falha
    try {
      const cachedResponses = await AsyncStorage.getItem(
        `cached_responses_avaliador_${avaliadorId}`
      );
      if (cachedResponses) {
        return JSON.parse(cachedResponses);
      }
    } catch (storageError) {
      console.error('❌ Erro ao buscar respostas do avaliador do cache:', storageError);
    }

    return []; // Retornar array vazio em caso de erro
  }
};

export const getResponseById = async id => {
  try {
    console.log(`🔄 Buscando resposta ${id}...`);
    const result = await fetchWithTimeout(`${API_URL}/responses/${id}`);

    // Transformar o resultado da API para o formato do app
    const formattedResponse = transformApiToResponse(result);

    // Atualizar cache local
    try {
      await AsyncStorage.setItem(`cached_response_${id}`, JSON.stringify(formattedResponse));
    } catch (storageError) {
      console.warn('⚠️ Erro ao atualizar cache local da resposta:', storageError);
    }

    return formattedResponse;
  } catch (error) {
    console.error(`❌ Erro ao buscar resposta ${id}:`, error);

    // Tentar ler do armazenamento local em caso de falha
    try {
      const cachedResponse = await AsyncStorage.getItem(`cached_response_${id}`);
      if (cachedResponse) {
        return JSON.parse(cachedResponse);
      }
    } catch (storageError) {
      console.error('❌ Erro ao buscar resposta do cache:', storageError);
    }

    throw error;
  }
};

// FUNÇÃO SAVERESPONSE TOTALMENTE CORRIGIDA COM TODAS AS ATUALIZAÇÕES SOLICITADAS
// FUNÇÃO SAVERESPONSE CORRIGIDA PARA ACEITAR RESPOSTAS PARCIAIS
export const saveResponse = async responseData => {
  try {
    console.log('📤 saveResponse iniciado:', {
      formId: responseData.formId,
      responseId: responseData.id,
      avaliadorId: responseData.avaliador?.id,
      avaliadorNome: responseData.avaliador?.nome,
      isComplete: responseData.isComplete,
      answeredCount: Object.keys(responseData.answers || {}).length,
      totalQuestions: responseData.totalQuestions,
    });

    // IMPORTANTE: Garantir que tem avaliador
    if (!responseData.avaliador || !responseData.avaliador.id) {
      throw new Error('Resposta sem avaliador não pode ser salva');
    }

    // CORREÇÃO: Aceitar respostas parciais - remover validação de completude
    // Apenas garantir que tem pelo menos 1 resposta
    if (!responseData.answers || Object.keys(responseData.answers).length === 0) {
      throw new Error('A resposta deve conter pelo menos uma pergunta respondida');
    }

    // Validação de totalQuestions
    if (!responseData.totalQuestions || responseData.totalQuestions === 0) {
      console.warn('⚠️ Resposta sem totalQuestions, tentando recuperar...');

      try {
        const form = await getFormById(responseData.formId);
        if (form && form.totalQuestions) {
          responseData.totalQuestions = form.totalQuestions;
        } else if (form && form.requirements) {
          responseData.totalQuestions = form.requirements.length;
        }
      } catch (formError) {
        console.error('❌ Erro ao buscar totalQuestions do formulário:', formError);
      }
    }

    // Transformar os dados
    const apiData = transformResponseToApi(responseData);
    apiData.totalQuestions = responseData.totalQuestions || 0;

    // CORREÇÃO: Sempre usar PUT se já existe resposta para este formulário + avaliador
    let result;
    let method = 'POST';
    let endpoint = `${API_URL}/responses`;

    try {
      const existingResponses = await fetchWithTimeout(
        `${API_URL}/responses/form/${responseData.formId}/avaliador/${responseData.avaliador.id}`,
        { method: 'GET' }
      );

      if (existingResponses && existingResponses.length > 0) {
        method = 'PUT';
        endpoint = `${API_URL}/responses/${existingResponses[0].id}`;
        console.log(`🔄 Atualizando resposta existente: ${existingResponses[0].id}`);
      } else {
        console.log('📝 Criando nova resposta');
      }
    } catch (checkError) {
      console.log('📝 Nenhuma resposta existente, criando nova...');
    }

    // Fazer a requisição
    result = await fetchWithTimeout(endpoint, {
      method: method,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(apiData),
    });

    // IMPORTANTE: Preservar dados de validação se houver
    if (method === 'PUT' && result.validacao && result.validacao.validadoPor) {
      console.log('✅ Preservando dados de validação existentes:', {
        validadoPor: result.validacao.validadoPor.nome,
        dataValidacao: result.validacao.dataValidacao,
      });
    }

    // Transformar resultado e marcar como sincronizado
    const formattedResult = transformApiToResponse(result);
    formattedResult.synced = true;
    formattedResult.syncedAt = new Date().toISOString();
    formattedResult.totalQuestions =
      formattedResult.totalQuestions || responseData.totalQuestions || 0;

    // Preservar campos de validação
    if (result.validacao && result.validacao.validadoPor) {
      formattedResult.validacao = result.validacao;
      console.log('✅ Dados de validação preservados do servidor');
    } else if (responseData.validacao) {
      formattedResult.validacao = responseData.validacao;
    } else {
      formattedResult.validacao = {
        validadoPor: null,
        dataValidacao: null,
        revalidacoes: [],
      };
    }

    // Atualizar cache
    await updateResponseCache({
      ...formattedResult,
      synced: true,
      syncedAt: formattedResult.syncedAt,
    });

    console.log('✅ saveResponse concluído:', {
      synced: formattedResult.synced,
      syncedAt: formattedResult.syncedAt,
      method: method,
      isPartial: !responseData.isComplete,
    });

    return {
      ...formattedResult,
      synced: true,
      syncedAt: formattedResult.syncedAt || new Date().toISOString(),
      totalQuestions: formattedResult.totalQuestions || responseData.totalQuestions || 0,
      syncStatus: 'synced',
    };
  } catch (error) {
    console.error('❌ Erro ao salvar resposta:', error);
    throw error;
  }
};

export const deleteResponse = async id => {
  try {
    console.log(`🔄 Excluindo resposta ${id} do servidor...`);

    const result = await fetchWithTimeout(`${API_URL}/responses/${id}`, {
      method: 'DELETE',
    });

    // Atualizar cache local
    try {
      await AsyncStorage.removeItem(`cached_response_${id}`);

      // Remover da lista de respostas
      const cachedResponses = await AsyncStorage.getItem('cached_responses');
      if (cachedResponses) {
        let responses = JSON.parse(cachedResponses);
        const responseToRemove = responses.find(r => r.id === id);

        responses = responses.filter(r => r.id !== id);
        await AsyncStorage.setItem('cached_responses', JSON.stringify(responses));

        // Remover também da lista de respostas do formulário
        if (responseToRemove) {
          const formId = responseToRemove.formId;
          const cachedFormResponses = await AsyncStorage.getItem(`cached_responses_form_${formId}`);
          if (cachedFormResponses) {
            let formResponses = JSON.parse(cachedFormResponses);
            formResponses = formResponses.filter(r => r.id !== id);
            await AsyncStorage.setItem(
              `cached_responses_form_${formId}`,
              JSON.stringify(formResponses)
            );
          }
        }
      }
    } catch (storageError) {
      console.warn('⚠️ Erro ao atualizar cache local:', storageError);
    }

    return result;
  } catch (error) {
    console.error(`❌ Erro ao excluir resposta ${id}:`, error);

    // Salvar localmente para sincronização posterior
    try {
      const pendingDeletes = (await AsyncStorage.getItem('pending_response_deletes')) || '[]';
      const deletes = JSON.parse(pendingDeletes);
      deletes.push({
        id: id,
        timestamp: Date.now(),
      });
      await AsyncStorage.setItem('pending_response_deletes', JSON.stringify(deletes));
      console.log('✅ Exclusão salva localmente para sincronização futura');
    } catch (storageError) {
      console.error('❌ Erro ao salvar exclusão localmente:', storageError);
    }

    throw error;
  }
};

// OPERAÇÕES RELACIONADAS A AVALIADORES
export const getAvaliadores = async () => {
  try {
    console.log('🔄 Buscando avaliadores no servidor...');
    return await fetchWithTimeout(`${API_URL}/avaliadores`);
  } catch (error) {
    console.error('❌ Erro ao buscar avaliadores:', error);

    // Tentar ler do armazenamento local em caso de falha
    try {
      const cachedAvaliadores = await AsyncStorage.getItem('cached_avaliadores');
      if (cachedAvaliadores) {
        return JSON.parse(cachedAvaliadores);
      }
    } catch (storageError) {
      console.error('❌ Erro ao buscar avaliadores do cache:', storageError);
    }

    throw error;
  }
};

export const getAvaliadorById = async id => {
  try {
    console.log(`🔄 Buscando avaliador ${id}...`);
    return await fetchWithTimeout(`${API_URL}/avaliadores/${id}`);
  } catch (error) {
    console.error(`❌ Erro ao buscar avaliador ${id}:`, error);

    // Tentar ler do armazenamento local em caso de falha
    try {
      const cachedAvaliadores = await AsyncStorage.getItem('cached_avaliadores');
      if (cachedAvaliadores) {
        const avaliadores = JSON.parse(cachedAvaliadores);
        const avaliador = avaliadores.find(a => a.id === id);
        if (avaliador) return avaliador;
      }
    } catch (storageError) {
      console.error('❌ Erro ao buscar avaliador do cache:', storageError);
    }

    throw error;
  }
};

export const createAvaliador = async avaliadorData => {
  try {
    console.log('🔄 Criando avaliador no servidor...');
    return await fetchWithTimeout(`${API_URL}/avaliadores`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(avaliadorData),
    });
  } catch (error) {
    console.error('❌ Erro ao criar avaliador:', error);
    throw error;
  }
};

// OPERAÇÕES RELACIONADAS A UNIDADES ORGANIZACIONAIS
export const getUnidadesOrganizacionais = async () => {
  try {
    console.log('🔄 Buscando unidades organizacionais no servidor...');
    return await fetchWithTimeout(`${API_URL}/unidades_organizacionais`);
  } catch (error) {
    console.error('❌ Erro ao buscar unidades organizacionais:', error);

    // Tentar ler do armazenamento local em caso de falha
    try {
      const cachedUnidades = await AsyncStorage.getItem('cached_unidades');
      if (cachedUnidades) {
        return JSON.parse(cachedUnidades);
      }
    } catch (storageError) {
      console.error('❌ Erro ao buscar unidades do cache:', storageError);
    }

    throw error;
  }
};

export const getUnidadeOrganizacionalById = async id => {
  try {
    console.log(`🔄 Buscando unidade organizacional ${id}...`);
    return await fetchWithTimeout(`${API_URL}/unidades_organizacionais/${id}`);
  } catch (error) {
    console.error(`❌ Erro ao buscar unidade organizacional ${id}:`, error);

    // Tentar ler do armazenamento local em caso de falha
    try {
      const cachedUnidades = await AsyncStorage.getItem('cached_unidades');
      if (cachedUnidades) {
        const unidades = JSON.parse(cachedUnidades);
        const unidade = unidades.find(u => u.id === id);
        if (unidade) return unidade;
      }
    } catch (storageError) {
      console.error('❌ Erro ao buscar unidade do cache:', storageError);
    }

    throw error;
  }
};

export const createUnidadeOrganizacional = async unidadeData => {
  try {
    console.log('🔄 Criando unidade organizacional no servidor...');
    return await fetchWithTimeout(`${API_URL}/unidades_organizacionais`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(unidadeData),
    });
  } catch (error) {
    console.error('❌ Erro ao criar unidade organizacional:', error);
    throw error;
  }
};

// OPERAÇÕES RELACIONADAS A ARQUIVOS NO SUPABASE VIA SERVIDOR
export const uploadFile = async (file, bucketName = 'avaqualisattachment', folderPath = '') => {
  try {
    console.log('📤 Iniciando upload de arquivo para o servidor...');

    if (!file || !file.url) {
      throw new Error('Arquivo inválido para upload');
    }

    // Criar FormData para envio multipart
    const formData = new FormData();

    // Converter URL para Blob no navegador web
    let fileBlob;

    if (typeof file.url === 'string') {
      if (file.url.startsWith('file:') || file.url.startsWith('content:')) {
        // Para dispositivos nativos, usar o arquivo diretamente
        formData.append('file', {
          url: file.url,
          name: file.name || `file_${Date.now()}${getFileExtension(file)}`,
          type: file.mimeType || file.type || 'application/octet-stream',
        });
      } else {
        // Para web, converter para blob
        const response = await fetch(file.url);
        fileBlob = await response.blob();
        formData.append(
          'file',
          fileBlob,
          file.name || `file_${Date.now()}${getFileExtension(file)}`
        );
      }
    } else {
      throw new Error('URL do arquivo inválida');
    }

    // Adicionar informações adicionais
    formData.append('bucketName', bucketName);
    formData.append('folderPath', folderPath);

    // Fazer upload para o servidor
    const response = await fetch(`${API_URL}/upload`, {
      method: 'POST',
      body: formData,
      // Não definir o content-type aqui, o navegador o definirá corretamente para FormData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    console.log('🎉 Upload concluído com sucesso!', result);

    return {
      path: result.path,
      url: result.url,
      bucket: bucketName,
    };
  } catch (error) {
    console.error('❌ Erro ao fazer upload de arquivo:', error);

    // Salvar localmente para tentar sincronizar depois
    try {
      const pendingFileUploads = (await AsyncStorage.getItem('pending_file_uploads')) || '[]';
      const fileUploads = JSON.parse(pendingFileUploads);
      fileUploads.push({
        file: {
          url: file.url,
          name: file.name,
          type: file.mimeType || file.type,
          size: file.size,
        },
        bucketName,
        folderPath,
        timestamp: Date.now(),
      });
      await AsyncStorage.setItem('pending_file_uploads', JSON.stringify(fileUploads));
      console.log('✅ Upload de arquivo salvo localmente para sincronização futura');
    } catch (storageError) {
      console.error('❌ Erro ao salvar upload de arquivo localmente:', storageError);
    }

    throw error;
  }
};

export const deleteFile = async (filePath, bucketName = 'avaqualisattachment') => {
  try {
    console.log(`🗑️ Tentando excluir arquivo: ${filePath} do bucket ${bucketName}`);

    const response = await fetch(`${API_URL}/files/${bucketName}/${filePath}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('✅ Arquivo excluído com sucesso!', result);

    return result;
  } catch (error) {
    console.error('❌ Erro ao excluir arquivo:', error);

    // Salvar localmente para tentar sincronizar depois
    try {
      const pendingFileDeletes = (await AsyncStorage.getItem('pending_file_deletes')) || '[]';
      const fileDeletes = JSON.parse(pendingFileDeletes);
      fileDeletes.push({
        filePath,
        bucketName,
        timestamp: Date.now(),
      });
      await AsyncStorage.setItem('pending_file_deletes', JSON.stringify(fileDeletes));
      console.log('✅ Exclusão de arquivo salva localmente para sincronização futura');
    } catch (storageError) {
      console.error('❌ Erro ao salvar exclusão de arquivo localmente:', storageError);
    }

    throw error;
  }
};

// NOVA FUNÇÃO: Obter respostas mais recentes por formulário
export const getLatestResponsesByForm = async formId => {
  try {
    console.log(`🔄 Buscando respostas mais recentes para formulário ${formId}...`);

    const responses = await fetchWithTimeout(`${API_URL}/responses/form/${formId}/latest`, {
      method: 'GET',
    });

    // Transformar respostas
    const formattedResponses = responses.map(r => transformApiToResponse(r));

    return formattedResponses;
  } catch (error) {
    console.error(`❌ Erro ao buscar respostas mais recentes:`, error);

    // Fallback: buscar todas e filtrar localmente
    try {
      const allResponses = await getResponsesByFormId(formId);

      // Agrupar por avaliador e pegar a mais recente
      const latestByAvaliador = {};

      allResponses.forEach(response => {
        const avaliadorId = response.avaliador?.id;
        if (!avaliadorId) return;

        if (
          !latestByAvaliador[avaliadorId] ||
          new Date(response.lastModified) > new Date(latestByAvaliador[avaliadorId].lastModified)
        ) {
          latestByAvaliador[avaliadorId] = response;
        }
      });

      return Object.values(latestByAvaliador);
    } catch (fallbackError) {
      console.error('❌ Erro no fallback:', fallbackError);
      return [];
    }
  }
};

export const listFiles = async (bucketName = 'avaqualisattachment', folderPath = '') => {
  try {
    const endpoint = folderPath
      ? `${API_URL}/files/${bucketName}/${folderPath}`
      : `${API_URL}/files/${bucketName}`;

    return await fetchWithTimeout(endpoint);
  } catch (error) {
    console.error('❌ Erro ao listar arquivos:', error);

    // Tentar ler do armazenamento local se falhar
    try {
      const cachedFiles = await AsyncStorage.getItem(`cached_files_${bucketName}_${folderPath}`);
      if (cachedFiles) {
        return JSON.parse(cachedFiles);
      }
    } catch (storageError) {
      console.error('❌ Erro ao buscar arquivos do cache:', storageError);
    }

    return []; // Retornar array vazio em caso de erro
  }
};

// FUNÇÃO PARA VERIFICAR SAÚDE DO SERVIDOR
export const checkServerHealth = async () => {
  try {
    console.log('🔍 Verificando conexão com o servidor:', `${API_URL}/health`);

    // Tentativa com timeout menor (3 segundos)
    const response = await fetch(`${API_URL}/health`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      // Não usar o signal aqui para evitar problemas com o AbortController
      timeout: 3000,
    });

    if (!response.ok) {
      console.warn(`⚠️ Servidor respondeu com status ${response.status}`);
      return {
        status: 'error',
        message: `Servidor respondeu com status ${response.status}`,
        timestamp: new Date().toISOString(),
      };
    }

    const data = await response.json();
    console.log('✅ Servidor online:', data);
    return data;
  } catch (error) {
    console.error('❌ Erro ao verificar saúde do servidor:', error);
    return {
      status: 'offline',
      error: error.message || 'Erro desconhecido',
      timestamp: new Date().toISOString(),
    };
  }
};

// FUNÇÃO PARA SINCRONIZAR DADOS PENDENTES
export const syncPendingData = async () => {
  console.log('🔄 Tentando sincronizar dados pendentes...');

  try {
    // Verificar se o servidor está online
    const health = await checkServerHealth();
    if (health.status !== 'online') {
      console.log('⚠️ Servidor offline, sincronização adiada');
      return false;
    }

    // Sincronizar uploads de formulários pendentes
    const pendingUploads = await AsyncStorage.getItem('pending_uploads');
    if (pendingUploads) {
      const uploads = JSON.parse(pendingUploads);
      if (uploads.length > 0) {
        console.log(`🔄 Sincronizando ${uploads.length} uploads pendentes...`);

        const newUploads = [];
        for (const upload of uploads) {
          try {
            if (upload.type === 'form') {
              await createForm(upload.data);
            }
          } catch (error) {
            console.error(`❌ Erro ao sincronizar upload ${upload.type}:`, error);
            newUploads.push(upload);
          }
        }

        // Atualizar lista de uploads pendentes
        if (newUploads.length > 0) {
          await AsyncStorage.setItem('pending_uploads', JSON.stringify(newUploads));
        } else {
          await AsyncStorage.removeItem('pending_uploads');
        }
      }
    }

    // Sincronizar uploads de respostas pendentes
    const pendingResponseUploads = await AsyncStorage.getItem('pending_response_uploads');
    if (pendingResponseUploads) {
      const uploads = JSON.parse(pendingResponseUploads);
      if (uploads.length > 0) {
        console.log(`🔄 Sincronizando ${uploads.length} respostas pendentes...`);

        const newUploads = [];
        for (const upload of uploads) {
          try {
            await saveResponse(upload.data);
          } catch (error) {
            console.error('❌ Erro ao sincronizar resposta:', error);
            newUploads.push(upload);
          }
        }

        // Atualizar lista de uploads pendentes
        if (newUploads.length > 0) {
          await AsyncStorage.setItem('pending_response_uploads', JSON.stringify(newUploads));
        } else {
          await AsyncStorage.removeItem('pending_response_uploads');
        }
      }
    }

    // Sincronizar exclusões completas de formulários pendentes
    const pendingCompleteDeletes = await AsyncStorage.getItem('pending_complete_deletes');
    if (pendingCompleteDeletes) {
      const deletes = JSON.parse(pendingCompleteDeletes);
      if (deletes.length > 0) {
        console.log(`🔄 Sincronizando ${deletes.length} exclusões completas pendentes...`);

        const newDeletes = [];
        for (const del of deletes) {
          try {
            if (del.type === 'form_complete') {
              await deleteFormComplete(del.formId);
            }
          } catch (error) {
            console.error(`❌ Erro ao sincronizar exclusão completa:`, error);
            newDeletes.push(del);
          }
        }

        // Atualizar lista de exclusões completas pendentes
        if (newDeletes.length > 0) {
          await AsyncStorage.setItem('pending_complete_deletes', JSON.stringify(newDeletes));
        } else {
          await AsyncStorage.removeItem('pending_complete_deletes');
        }
      }
    }

    // Sincronizar atualizações de avaliadores pendentes
    const pendingEvaluatorUpdates = await AsyncStorage.getItem('pending_evaluator_updates');
    if (pendingEvaluatorUpdates) {
      const updates = JSON.parse(pendingEvaluatorUpdates);
      if (updates.length > 0) {
        console.log(`🔄 Sincronizando ${updates.length} atualizações de avaliadores pendentes...`);

        const newUpdates = [];
        for (const update of updates) {
          try {
            if (update.type === 'form_evaluators') {
              await updateFormEvaluators(update.formId, update.avaliador1Id, update.avaliador2Id);
            }
          } catch (error) {
            console.error(`❌ Erro ao sincronizar atualização de avaliadores:`, error);
            newUpdates.push(update);
          }
        }

        // Atualizar lista de atualizações de avaliadores pendentes
        if (newUpdates.length > 0) {
          await AsyncStorage.setItem('pending_evaluator_updates', JSON.stringify(newUpdates));
        } else {
          await AsyncStorage.removeItem('pending_evaluator_updates');
        }
      }
    }

    // Sincronizar exclusões de respostas por avaliador pendentes
    const pendingEvaluatorDeletes = await AsyncStorage.getItem('pending_evaluator_deletes');
    if (pendingEvaluatorDeletes) {
      const deletes = JSON.parse(pendingEvaluatorDeletes);
      if (deletes.length > 0) {
        console.log(
          `🔄 Sincronizando ${deletes.length} exclusões de respostas por avaliador pendentes...`
        );

        const newDeletes = [];
        for (const del of deletes) {
          try {
            if (del.type === 'responses_by_evaluator') {
              await deleteResponsesByEvaluator(del.formId, del.avaliadorId);
            }
          } catch (error) {
            console.error(`❌ Erro ao sincronizar exclusão de respostas por avaliador:`, error);
            newDeletes.push(del);
          }
        }

        // Atualizar lista de exclusões por avaliador pendentes
        if (newDeletes.length > 0) {
          await AsyncStorage.setItem('pending_evaluator_deletes', JSON.stringify(newDeletes));
        } else {
          await AsyncStorage.removeItem('pending_evaluator_deletes');
        }
      }
    }

    console.log('✅ Sincronização concluída com sucesso!');
    return true;
  } catch (error) {
    console.error('❌ Erro ao sincronizar dados pendentes:', error);
    return false;
  }
};

// FUNÇÕES AUXILIARES
const getFileExtension = file => {
  if (file.name) {
    const parts = file.name.split('.');
    return parts.length > 1 ? `.${parts[parts.length - 1]}` : '';
  }

  if (file.mimeType || file.type) {
    const mimeType = file.mimeType || file.type;
    const mimeToExt = {
      'image/jpeg': '.jpg',
      'image/jpg': '.jpg',
      'image/png': '.png',
      'image/gif': '.gif',
      'image/webp': '.webp',
      'application/pdf': '.pdf',
      'application/zip': '.zip',
      'application/x-zip-compressed': '.zip',
      'text/plain': '.txt',
      'application/msword': '.doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
      'video/mp4': '.mp4',
      'video/avi': '.avi',
      'video/mov': '.mov',
    };
    return mimeToExt[mimeType.toLowerCase()] || '.bin';
  }

  return '';
};

// ADICIONAR ESTAS FUNÇÕES NO FINAL DO ARQUIVO api.js ANTES DO export default

// ==================== OPERAÇÕES DE STREAMING ====================

// Criar novo stream
export const createStream = async (formId, formTitle, avaliador) => {
  try {
    console.log('🎥 Criando novo stream...', { formId, avaliador: avaliador?.id });

    const requestData = {
      formId,
      formTitle,
      avaliador,
    };

    console.log('📤 Dados enviados para criar stream:', requestData);

    const result = await fetchWithTimeout(`${API_URL}/streams`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData),
    });

    console.log('✅ Stream criado:', result);

    // Salvar informações do stream localmente
    await AsyncStorage.setItem(
      `active_stream_${formId}_${avaliador.id}`,
      JSON.stringify({
        streamId: result.streamId,
        shareLink: result.shareLink,
        expiresAt: result.expiresAt,
        createdAt: new Date().toISOString(),
      })
    );

    return result;
  } catch (error) {
    console.error('❌ Erro ao criar stream:', error);
    console.error('Stack trace:', error.stack);
    throw error;
  }
};

// Atualizar dados do stream - VERSÃO CORRIGIDA
export const updateStream = async (streamId, updateData) => {
  try {
    // Log mais detalhado para debug
    const logData = {
      streamId: streamId,
      hasCurrentQuestion: !!updateData.currentQuestion,
      hasCurrentAnswer: updateData.currentAnswer !== undefined,
      hasCurrentFindings: updateData.currentFindings !== undefined,
      questionStatus: updateData.questionStatus,
      dataKeys: Object.keys(updateData),
    };

    // Só mostrar log para atualizações importantes
    if (updateData.currentQuestion || updateData.questionStatus || updateData.progress) {
      console.log('🔄 Atualizando stream...', logData);
    }

    // Garantir que os dados estão serializáveis
    const cleanData = JSON.parse(JSON.stringify(updateData));

    const result = await fetchWithTimeout(
      `${API_URL}/streams/${streamId}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(cleanData),
      },
      10000
    ); // Aumentar timeout para 10 segundos

    // Log de sucesso apenas para atualizações importantes
    if (updateData.currentQuestion || updateData.questionStatus) {
      console.log('✅ Stream atualizado com sucesso');
    }

    return result;
  } catch (error) {
    // Tratar diferentes tipos de erro
    if (error.message.includes('timeout')) {
      console.warn('⚠️ Timeout na atualização do stream');
      // Não propagar erro de timeout para digitação
      if (
        updateData.currentFindings !== undefined ||
        updateData.notApplicableReason !== undefined
      ) {
        return { success: false, timeout: true };
      }
    } else if (error.message.includes('Failed to fetch')) {
      console.error('🌐 Erro de rede ao atualizar stream');
    } else {
      console.error('❌ Erro ao atualizar stream:', error);
      console.error('Dados que causaram erro:', updateData);
    }
    throw error;
  }
};

// Buscar dados do stream
export const getStream = async streamId => {
  try {
    console.log(`🔍 Buscando stream ${streamId}...`);

    const result = await fetchWithTimeout(`${API_URL}/streams/${streamId}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    console.log('✅ Stream encontrado:', {
      streamId: result.streamId,
      active: result.active,
      hasCurrentData: !!result.currentData,
    });

    return result;
  } catch (error) {
    console.error('❌ Erro ao buscar stream:', error);
    throw error;
  }
};

// Desativar stream
export const deactivateStream = async streamId => {
  try {
    console.log(`🛑 Desativando stream ${streamId}...`);

    const result = await fetchWithTimeout(`${API_URL}/streams/${streamId}`, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    console.log('✅ Stream desativado com sucesso');

    // Remover do armazenamento local
    const keys = await AsyncStorage.getAllKeys();
    const streamKeys = keys.filter(key => key.includes(streamId));
    if (streamKeys.length > 0) {
      await AsyncStorage.multiRemove(streamKeys);
    }

    return result;
  } catch (error) {
    console.error('❌ Erro ao desativar stream:', error);
    throw error;
  }
};

// Verificar se existe stream ativo
export const checkActiveStream = async (formId, avaliadorId) => {
  try {
    console.log(`🔍 Verificando stream ativo para formulário ${formId}...`);

    const result = await fetchWithTimeout(`${API_URL}/streams/check/${formId}/${avaliadorId}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    // Também verificar no armazenamento local
    if (!result.hasActiveStream) {
      const localStream = await AsyncStorage.getItem(`active_stream_${formId}_${avaliadorId}`);

      if (localStream) {
        const streamData = JSON.parse(localStream);
        const expiresAt = new Date(streamData.expiresAt);

        if (expiresAt > new Date()) {
          return {
            hasActiveStream: true,
            streamId: streamData.streamId,
            shareLink: streamData.shareLink,
            expiresAt: streamData.expiresAt,
          };
        } else {
          // Stream expirado, remover
          await AsyncStorage.removeItem(`active_stream_${formId}_${avaliadorId}`);
        }
      }
    }

    return result;
  } catch (error) {
    console.error('❌ Erro ao verificar stream ativo:', error);

    // Tentar verificar localmente em caso de falha
    try {
      const localStream = await AsyncStorage.getItem(`active_stream_${formId}_${avaliadorId}`);

      if (localStream) {
        const streamData = JSON.parse(localStream);
        const expiresAt = new Date(streamData.expiresAt);

        if (expiresAt > new Date()) {
          return {
            hasActiveStream: true,
            streamId: streamData.streamId,
            shareLink: streamData.shareLink,
            expiresAt: streamData.expiresAt,
          };
        }
      }
    } catch (localError) {
      console.error('❌ Erro ao verificar stream local:', localError);
    }

    return { hasActiveStream: false };
  }
};

// Polling para atualizações do stream - VERSÃO MELHORADA
export const pollStreamUpdates = async (streamId, lastUpdate) => {
  try {
    const params = lastUpdate ? `?lastUpdate=${lastUpdate}` : '';

    // Usar timeout menor que o servidor
    const result = await fetchWithTimeout(
      `${API_URL}/streams/${streamId}/poll${params}`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      },
      25000 // 25 segundos (menos que o servidor)
    );

    return result;
  } catch (error) {
    // Se for timeout, retornar sem update
    if (error.message.includes('timeout') || error.message.includes('Tempo limite')) {
      console.log('⏱️ Timeout no polling, continuando...');
      return { hasUpdate: false };
    }

    // Se for erro de rede, tentar novamente
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      console.log('🌐 Erro de rede no polling, retornando sem update');
      return { hasUpdate: false };
    }

    console.error('❌ Erro no polling:', error);
    throw error;
  }
};

// Função auxiliar para debounce de atualizações - VERSÃO MELHORADA
let updateStreamTimeout = null;
let pendingUpdates = {};
let updatePromiseResolve = null;
let updatePromiseReject = null;

export const updateStreamDebounced = (streamId, updateData, immediate = false) => {
  // Mesclar atualizações pendentes
  pendingUpdates = {
    ...pendingUpdates,
    ...updateData,
  };

  // Limpar timeout anterior
  if (updateStreamTimeout) {
    clearTimeout(updateStreamTimeout);
  }

  // Se for imediato ou mudança importante, enviar agora
  if (
    immediate ||
    updateData.currentQuestion ||
    updateData.currentAnswer ||
    updateData.questionStatus ||
    updateData.progress
  ) {
    const updates = { ...pendingUpdates };
    pendingUpdates = {};

    // Cancelar promise pendente se existir
    if (updatePromiseReject) {
      updatePromiseReject(new Error('Cancelado por nova atualização imediata'));
      updatePromiseResolve = null;
      updatePromiseReject = null;
    }

    return updateStream(streamId, updates).catch(error => {
      // Não propagar erro para atualizações não críticas
      if (error.message.includes('timeout') && !immediate) {
        console.warn('⚠️ Timeout em atualização não crítica, ignorando');
        return { success: false, timeout: true };
      }
      throw error;
    });
  }

  // Caso contrário, aguardar um pouco para agrupar atualizações
  return new Promise((resolve, reject) => {
    // Cancelar promise anterior se existir
    if (updatePromiseReject) {
      updatePromiseReject(new Error('Cancelado por nova atualização'));
    }

    updatePromiseResolve = resolve;
    updatePromiseReject = reject;

    updateStreamTimeout = setTimeout(async () => {
      try {
        const updates = { ...pendingUpdates };
        pendingUpdates = {};

        const result = await updateStream(streamId, updates);

        if (updatePromiseResolve) {
          updatePromiseResolve(result);
        }
      } catch (error) {
        if (updatePromiseReject) {
          // Não rejeitar para erros de timeout em digitação
          if (
            error.message.includes('timeout') &&
            (updates.currentFindings !== undefined || updates.notApplicableReason !== undefined)
          ) {
            updatePromiseResolve({ success: false, timeout: true });
          } else {
            updatePromiseReject(error);
          }
        }
      } finally {
        updatePromiseResolve = null;
        updatePromiseReject = null;
      }
    }, 500); // 500ms de debounce para digitação
  });
};

// ADICIONAR NO export default NO FINAL DO ARQUIVO:
/*
  // Streaming
  createStream,
  updateStream,
  updateStreamDebounced,
  getStream,
  deactivateStream,
  checkActiveStream,
  pollStreamUpdates
*/

// Exportar todas as funções como um objeto
export default {
  // Forms (Formulários)
  getForms,
  getFormById,
  createForm,
  updateForm,
  deleteForm,
  deleteFormComplete,
  updateFormEvaluators,
  updateFormMetadata,
  sendLeadershipEmail,

  // Responses (Respostas)
  getResponses,
  getResponsesByFormId,
  getResponsesByFormAndAvaliador, // FUNÇÃO CORRIGIDA ADICIONADA
  getDetailedSyncStatus, // NOVA FUNÇÃO ADICIONADA
  getResponsesByAvaliadorId,
  getResponseById,
  saveResponse, // FUNÇÃO TOTALMENTE CORRIGIDA - PROBLEMA CRÍTICO RESOLVIDO
  deleteResponse,
  deleteResponsesByEvaluator,
  checkResponseExists, // NOVA FUNÇÃO ADICIONADA
  getResponseSyncStatus, // NOVA FUNÇÃO ADICIONADA
  getLatestResponsesByForm,

  // Avaliadores
  getAvaliadores,
  getAvaliadorById,
  createAvaliador,

  // Unidades Organizacionais
  getUnidadesOrganizacionais,
  getUnidadeOrganizacionalById,
  createUnidadeOrganizacional,

  // Arquivo e Supabase
  uploadFile,
  deleteFile,
  listFiles,

  // Utilitários
  checkServerHealth,
  syncPendingData,

  // Streaming
  createStream,
  updateStream,
  updateStreamDebounced,
  getStream,
  deactivateStream,
  checkActiveStream,
  pollStreamUpdates,

  // Processos
  getProcessos,
  updateProcessoNome,

  // Validação
  getResponsesPendingValidation,
  getValidatedResponses,
  validateResponse,
};
